import {Component, Input, ViewChild} from '@angular/core';
import {DomSanitizer} from '@angular/platform-browser';
import {catchError, forkJoin, map, Observable, of, switchMap, tap} from 'rxjs';
import {PersonUtils} from 'src/app/auth_profile/utils/v3/person-utils';
import {ModalComponent} from 'src/app/utils/modal/modal.component';
import {SpinnerService} from 'src/app/utils/services/spinner.service';
import {LogsService} from "../../../../utils/services/logs.service";
import {NativeServiceApiProvider} from "../../../../services/native-api-provider.service";
import * as LessonsV3 from "../../../model/v3/lessons";
import {PersonContactCacheService} from "../../../services/person-contact-cache.service";
import {AuthorizationServiceProvider} from "../../../../auth_profile/services/authorization-service.provider";
import {TeacherV3RestService} from "../../../../auth_profile/services/api/v3/teacher-rest.service";
import {LearningUnitTeacherStatus, V3Teacher} from "../../../../auth_profile/model/v3/schools";
import {CourseProductMapper} from "../../../utils/course-product-mapper";

@Component({
    selector: 'app-teachers-list',
    templateUrl: './teachers-list.component.html',
    styleUrls: ['./teachers-list.component.scss'],
    standalone: false
})
export class TeachersListComponent {
  _langCode: string;
  _studentId: number;
  contextTeachers: V3Teacher[] = [];
  selectedTeacher: V3Teacher;
  private schoolId: number;
  private teacherProfilesById = new Map<number, LessonsV3.V3CourseTeacherProfile[]>();

  @ViewChild('teacherDetailsModal', { static: true })
  teacherDetailsModal: ModalComponent;
  @Input()
  set langCode(langCode: string) {
    let langChange = langCode != this._langCode;
    this._langCode = langCode;
    if(langChange)
      this.reloadTeachers();
  }

  @Input()
  set studentId(studentId: number) {
    this._studentId = studentId;
    this.reloadTeachers();
  }
  constructor(
    private spinner: SpinnerService,
    private sanitizer: DomSanitizer,
    private logger: LogsService,
    private provider: NativeServiceApiProvider,
    private personContactCache: PersonContactCacheService,
    private authService: AuthorizationServiceProvider,
    private teacherV3RestService: TeacherV3RestService
  ) {}

  private reloadTeachers(): void {
    this.loadTeachers().subscribe({
      error: (error) => {
        this.logger.error('failed to load teachers list', error);
        this.contextTeachers = [];
      },
    });
  }

  loadTeachers(): Observable<V3Teacher[] | null> {
    this.logger.log(`loading teachers for studentId=${this._studentId} and language=${this._langCode}`)
    if (this._langCode == null || !this._studentId) return of(null);
    return this.spinner.trace<V3Teacher[]>(
      this.authService.getAuthDetailsService().pipe(
        switchMap((auth) => auth.getSelfSchoolId()),
        tap((schoolId) => this.schoolId = schoolId),
        switchMap((schoolId) =>
          this.teacherV3RestService.findTeachers(
            schoolId,
            null,
            this._langCode
          )
        ),
        map((page) => page?.content ?? []),
        switchMap((teachers) => {
          if (!teachers?.length) return of([]);
          return forkJoin(
            teachers.map((teacher) =>
              this.getTeacherCompetences(teacher).pipe(
                catchError((error) => {
                  this.logger.error(`failed to load competences for teacherId=${teacher?.id}`, error);
                  return of([]);
                }),
                tap((profiles) => this.teacherProfilesById.set(teacher.id, profiles)),
                map(() => teacher)
              )
            )
          );
        }),
          tap(
            (teachers) =>
              (this.contextTeachers = teachers?.filter((teacher) =>
                teacher.status == LearningUnitTeacherStatus.ACTIVE
              ))
          ),
        switchMap((teachers) =>
            this.preloadTeacherPhotos(teachers ?? []).pipe(map(() => teachers))
          )
        )
    );
  }

  private getTeacherCompetences(teacher: V3Teacher): Observable<LessonsV3.V3CourseTeacherProfile[]> {
    const schoolId = this.schoolId;
    const teacherId = teacher?.id;
    if (!schoolId || !teacherId) {
      return of([]);
    }
    return this.provider.colV3().pipe(
      switchMap((api) => api.getTeacherCourseProfiles(schoolId, teacherId))
    );
  }

  private mapCourseCodeToProductCode(courseCode?: string | null): string {
    return CourseProductMapper.toProductCode(courseCode);
  }

  getName(teacherProfile: V3Teacher) {
    return PersonUtils.getPersonName(teacherProfile.person.details);
  }

  getTeacherPhoto(teacherProfile: V3Teacher): string | null {
    return this.personContactCache.getPhotoUrlSync(teacherProfile?.person?.id);
  }

  getInitials(teacherProfile: V3Teacher) {
    return PersonUtils.getInitials(teacherProfile.person.details);
  }

  hasImage(teacherProfile: V3Teacher) {
    return !!this.getTeacherPhoto(teacherProfile);
  }

  showTeacherDetails(teacherProfile: V3Teacher) {
    this.selectedTeacher = teacherProfile;
    this.teacherDetailsModal.show();
  }

  getCompetence(): LessonsV3.V3CourseTeacherProfile | null {
    const profiles = this.teacherProfilesById.get(this.selectedTeacher?.id) ?? [];
    const matching = profiles.find((p) => this.mapCourseCodeToProductCode(p?.course?.code) === this._langCode);
    return matching ?? profiles[0] ?? null;
  }

  hasSafeVideoUrl(): boolean {
    const videoUrl = this.getCompetence()?.videoUrl;
    return !!videoUrl && videoUrl.startsWith('https');
  }

  getVideoUrl() {
    const videoUrl = this.getCompetence()?.videoUrl;
    if (!videoUrl) {
      return null;
    }
    const safeUrl = videoUrl.replace(
      'watch?v=',
      'embed/'
    );
    return this.sanitizer.bypassSecurityTrustResourceUrl(safeUrl);
  }

  private preloadTeacherPhotos(teachers: V3Teacher[]): Observable<void> {
    if (!teachers?.length) {
      return of(void 0);
    }
    return forkJoin(
      teachers.map((teacher) =>
        this.personContactCache.getPhotoUrl(teacher?.person?.id).pipe(map(() => null))
      )
    ).pipe(map(() => void 0));
  }
}
